import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import LacunaWebPKI from 'web-pki';
import { WEB_PKI_LICENSE } from './web-pki-license';

export interface CertificadoInfo {
  thumbprint: string;
  subjectName: string;
  issuerName: string;
  validityStart?: string;
  validityEnd?: string;
}

@Injectable({ providedIn: 'root' })
export class WebPkiService {
  private pki: LacunaWebPKI = new LacunaWebPKI(WEB_PKI_LICENSE);
  private inicializado = false;

  init(): Observable<void> {
    return new Observable((sub) => {
      if (this.inicializado) {
        sub.next();
        sub.complete();
        return;
      }
      this.pki.init({
        ready: () => {
          this.inicializado = true;
          sub.next();
          sub.complete();
        },
        notInstalled: () => {
          sub.error({
            code: 'NOT_INSTALLED',
            message:
              'A extensão Lacuna Web PKI não está instalada. Instale a extensão no navegador e recarregue a página.',
          });
        },
        defaultFail: (ex: any) => {
          sub.error({
            code: 'INIT_ERROR',
            message: ex?.userMessage ?? ex?.message ?? 'Falha ao inicializar Web PKI',
            raw: ex,
          });
        },
      });
    });
  }

  listarCertificados(): Observable<CertificadoInfo[]> {
    return new Observable((sub) => {
      this.pki.listCertificates()
        .success((certs: any[]) => {
          const lista: CertificadoInfo[] = (certs ?? []).map((c) => ({
            thumbprint: c.thumbprint,
            subjectName: c.subjectName,
            issuerName: c.issuerName,
            validityStart: c.validityStart,
            validityEnd: c.validityEnd,
          }));
          sub.next(lista);
          sub.complete();
        })
        .fail((ex: any) => {
          sub.error({
            code: 'LIST_ERROR',
            message: ex?.userMessage ?? ex?.message ?? 'Falha ao listar certificados',
            raw: ex,
          });
        });
    });
  }

  lerCertificado(thumbprint: string): Observable<string> {
    return new Observable((sub) => {
      this.pki.readCertificate({ thumbprint })
        .success((certContent: string) => {
          sub.next(certContent);
          sub.complete();
        })
        .fail((ex: any) => {
          sub.error({
            code: 'READ_CERT_ERROR',
            message: ex?.userMessage ?? ex?.message ?? 'Falha ao ler certificado',
            raw: ex,
          });
        });
    });
  }

  assinarHash(thumbprint: string, hashBase64: string): Observable<string> {
    return new Observable((sub) => {
      this.pki.signHash({
        thumbprint,
        hash: hashBase64,
        digestAlgorithm: 'SHA-256',
      })
        .success((signature: string) => {
          sub.next(signature);
          sub.complete();
        })
        .fail((ex: any) => {
          sub.error({
            code: 'SIGN_ERROR',
            message: ex?.userMessage ?? ex?.message ?? 'Falha ao assinar com o certificado',
            raw: ex,
          });
        });
    });
  }
}
